import { supabase } from "@/integrations/supabase/client";

export interface SourceRef {
  source_type?: string | null;
  source_id?: string | null;
}

export interface ResolvedSourceMap {
  // key = `${type}:${id}` -> name
  [key: string]: string;
}

/**
 * Batch-resolve names for a list of (source_type, source_id) pairs against the
 * correct table (agents / suppliers / farms / user_profiles). Procurements use
 * a polymorphic source_type+source_id design with no FK to those tables, so we
 * can't use PostgREST embeds here.
 */
export async function resolveSourceNames(
  refs: SourceRef[]
): Promise<ResolvedSourceMap> {
  const buckets: Record<string, Set<string>> = {
    agent: new Set(),
    supplier: new Set(),
    farm: new Set(),
    harvest: new Set(),
    coordinator: new Set(),
  };

  for (const r of refs) {
    if (!r?.source_id || !r?.source_type) continue;
    const sourceType = r.source_type.trim().toLowerCase();
    if (buckets[sourceType]) buckets[sourceType].add(r.source_id);
  }

  const map: ResolvedSourceMap = {};

  const fetchers: Promise<void>[] = [];

  if (buckets.agent.size) {
    fetchers.push(
      (async () => {
        const ids = Array.from(buckets.agent);
        const { data } = await supabase
          .from("agents")
          .select("id, name")
          .in("id", ids);
        (data || []).forEach((row: any) => {
          map[`agent:${row.id}`] = row.name;
        });
      })()
    );
  }

  if (buckets.supplier.size) {
    fetchers.push(
      (async () => {
        const ids = Array.from(buckets.supplier);
        const { data } = await supabase
          .from("suppliers")
          .select("id, name")
          .in("id", ids);
        (data || []).forEach((row: any) => {
          map[`supplier:${row.id}`] = row.name;
        });
      })()
    );
  }

  if (buckets.farm.size) {
    fetchers.push(
      (async () => {
        const ids = Array.from(buckets.farm);
        const { data } = await supabase
          .from("farms")
          .select("id, farmer_name")
          .in("id", ids);
        (data || []).forEach((row: any) => {
          map[`farm:${row.id}`] = row.farmer_name;
        });
      })()
    );
  }

  const profileIds = new Set([...buckets.harvest, ...buckets.coordinator]);
  if (profileIds.size) {
    fetchers.push(
      (async () => {
        const ids = Array.from(profileIds);
        const { data } = await supabase
          .from("user_profiles")
          .select("id, first_name, last_name, email")
          .in("id", ids);
        (data || []).forEach((row: any) => {
          const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || row.email;
          if (buckets.harvest.has(row.id)) map[`harvest:${row.id}`] = name;
          if (buckets.coordinator.has(row.id)) map[`coordinator:${row.id}`] = name;
        });
      })()
    );
  }

  await Promise.all(fetchers);

  return map;
}

export function getSourceName(
  map: ResolvedSourceMap,
  ref: SourceRef
): string | null {
  if (!ref?.source_type || !ref?.source_id) return null;
  return map[`${ref.source_type.trim().toLowerCase()}:${ref.source_id}`] || null;
}
